import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  HiOutlineSparkles,
  HiOutlineArrowUp,
  HiOutlineShoppingBag,
  HiOutlineCreditCard,
  HiOutlineShieldCheck,
  HiOutlineXMark,
} from "react-icons/hi2";
import Button from "components/button/Button";
import { useUpgradePlanLicenseMutation } from "reduxFiles/api/licenseApi";
import { motion, AnimatePresence } from "framer-motion";

interface UpgradePlanMessageProps {
  assessmentId: number;
}

interface PlanData {
  amount: number;
  authority: string;
  redirectUrl: string;
  transactionId: number;
}

export default function UpgradePlanMessage({
  assessmentId,
}: UpgradePlanMessageProps) {
  const { t } = useTranslation();
  const [upgradePlan, { isLoading }] = useUpgradePlanLicenseMutation();
  const [planData, setPlanData] = useState<PlanData | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const handleUpgrade = async () => {
    if (planData) {
      setShowPaymentModal(true);
      return;
    }

    if (isLoading) return;

    try {
      const result = await upgradePlan({
        assessmentId,
        callback: window.location.href ?? "",
      }).unwrap();
      setPlanData(result);
      setShowPaymentModal(true);
    } catch (error) {
      console.error("Error upgrading plan:", error);
    }
  };

  const handleRedirectToPayment = () => {
    if (planData?.redirectUrl) {
      window.location.href = planData.redirectUrl;
    }
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
        <div className="relative">
          <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-brand-500 to-purple-500 opacity-20 blur-xl" />
          <div className="relative rounded-full bg-gradient-to-br from-brand-100 to-purple-100 p-4 dark:from-brand-900/30 dark:to-purple-900/30">
            <HiOutlineSparkles className="h-12 w-12 text-brand-600 dark:text-brand-400" />
          </div>
        </div>

        <h3 className="mt-6 text-xl font-bold text-navy-800 dark:text-white">
          {t("assessment.report.upgradeRequired.title")}
        </h3>

        <p className="mt-3 max-w-md text-sm text-gray-700 dark:text-gray-400">
          {t("assessment.report.upgradeRequired.description")}
        </p>

        <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row">
          <Button
            variant="primary"
            size="lg"
            onClick={handleUpgrade}
            loading={isLoading}
            startIcon={<HiOutlineArrowUp className="h-5 w-5" />}
          >
            {t("assessment.report.upgradeRequired.upgradeButton")}
          </Button>
        </div>
      </div>

      <AnimatePresence>
        {showPaymentModal && planData && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-black/50 fixed inset-0 z-50 backdrop-blur-sm"
              onClick={() => setShowPaymentModal(false)}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-navy-800">
                {/* Header gradient */}
                <div className="absolute left-0 right-0 top-0 h-1.5 bg-gradient-to-r from-brand-500 via-purple-500 to-brand-300" />

                {/* Close button */}
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="absolute right-4 top-4 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-navy-700 dark:hover:text-gray-300"
                >
                  <HiOutlineXMark className="h-5 w-5" />
                </button>

                <div className="p-6 pt-8">
                  {/* Icon */}
                  <div className="to-emerald-100 dark:to-emerald-900/30 mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-green-100 dark:from-green-900/30">
                    <HiOutlineShoppingBag className="h-8 w-8 text-green-600 dark:text-green-400" />
                  </div>

                  {/* Title */}
                  <h3 className="mt-4 text-center text-xl font-bold text-navy-800 dark:text-white">
                    {t("assessment.report.upgradeRequired.payment.title")}
                  </h3>

                  <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                    {t("assessment.report.upgradeRequired.payment.description")}
                  </p>

                  {/* Payment Details */}
                  <div className="mt-6 space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-navy-700 dark:bg-navy-700/50">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {t(
                          "assessment.report.upgradeRequired.payment.transactionId"
                        )}
                      </span>
                      <span className="font-mono text-sm text-navy-800 dark:text-white">
                        #{planData.transactionId}
                      </span>
                    </div>

                    <div className="flex items-start gap-2 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                      <HiOutlineShieldCheck className="mt-0.5 h-5 w-5 text-blue-600 dark:text-blue-400" />
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        {t(
                          "assessment.report.upgradeRequired.payment.security"
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="mt-4 flex items-center justify-between rounded-xl bg-brand-50 p-3 dark:bg-brand-900/20">
                    <span className="font-semibold text-brand-700 dark:text-brand-300">
                      {t("assessment.report.upgradeRequired.payment.total")}
                    </span>
                    <span className="text-xl font-bold text-brand-700 dark:text-brand-300">
                      {planData.amount?.toLocaleString()}{" "}
                      {t("actions.priceInit")}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="mt-6 flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowPaymentModal(false)}
                    >
                      {t("buttons.cancel")}
                    </Button>
                    <Button
                      variant="primary"
                      className="flex-1"
                      onClick={handleRedirectToPayment}
                      startIcon={<HiOutlineCreditCard className="h-5 w-5" />}
                    >
                      {t("assessment.report.upgradeRequired.payment.payButton")}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
